import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { Mic, Users, X, MonitorSpeaker, Headphones, Signal, Edit3, Check, Loader2 } from 'lucide-react';
import clsx from 'clsx';
import type { VoiceChannel } from '@/types';
import { VoiceParticipant, type Participant } from '@/components/voice/VoiceParticipant';
import { VoiceControls } from '@/components/voice/VoiceControls';
import { updateVoiceChannel } from '@/db/projects';
import { useAppStore } from '@/store/appStore';
import { useAuth } from '@/context/AuthContext';
import { handleError } from '@/utils/errorHandler';

interface VoiceChannelViewProps {
  channel: VoiceChannel;
  onJoin?: (channelId: string) => Promise<void>;
  onLeave?: (channelId: string) => Promise<void>;
  onClose?: () => void;
}

export function VoiceChannelView({ channel, onJoin, onLeave, onClose }: VoiceChannelViewProps) {
  const { user } = useAuth();
  const CURRENT_USER = useMemo(() => ({ id: user?.id || 'current-user', name: user?.user_metadata?.full_name || 'Utente' }), [user?.id, user?.user_metadata?.full_name]);
  const updateCurrentItem = useAppStore((state) => state.updateCurrentItem);
  const [isConnected, setIsConnected] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isDeafened, setIsDeafened] = useState(false);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [speakingUserId, setSpeakingUserId] = useState<string | null>(null);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [title, setTitle] = useState(channel.name);
  const [isSaving, setIsSaving] = useState(false);
  const titleInputRef = useRef<HTMLInputElement>(null);

  // Sync title with prop changes
  useEffect(() => {
  setTitle(channel.name);
  }, [channel.name]);

  // Focus title input when editing
  useEffect(() => {
  if (isEditingTitle && titleInputRef.current) {
      titleInputRef.current.focus();
      titleInputRef.current.select();
  }
  }, [isEditingTitle]);

  // Update participants based on connection state
  useEffect(() => {
  if (isConnected) {
      const currentUserParticipant: Participant = {
    id: CURRENT_USER.id,
    name: CURRENT_USER.name,
    isMuted,
    isDeafened,
    isSpeaking: speakingUserId === CURRENT_USER.id,
    isHost: false,
      };
      setParticipants([currentUserParticipant]);
  } else {
      setParticipants([]);
  }
  }, [isConnected, isMuted, isDeafened, speakingUserId, CURRENT_USER]);

  // Reset speaking state when muted or disconnected
  useEffect(() => {
  if (!isConnected || isMuted) {
      setSpeakingUserId(null);
  }
  }, [isConnected, isMuted]);

  const handleConnect = useCallback(async () => {
  if (onJoin) {
      await onJoin(channel.id);
  }
  setIsConnected(true);
  setIsMuted(false);
  setIsDeafened(false);
  }, [channel.id, onJoin]);

  const handleDisconnect = useCallback(async () => {
  if (onLeave) {
      await onLeave(channel.id);
  }
  setIsConnected(false);
  setIsMuted(false);
  setIsDeafened(false);
  setIsScreenSharing(false);
  }, [channel.id, onLeave]);

  const handleToggleMute = useCallback(() => {
  setIsMuted(prev => !prev);
  }, []);

  const handleToggleDeafen = useCallback(() => {
  setIsDeafened(prev => {
      // When deafening, also mute
      if (!prev) {
    setIsMuted(true);
      }
      return !prev;
  });
  }, []);

  const handleScreenShare = useCallback(() => {
  setIsScreenSharing(prev => !prev);
  }, []);

  // Save title handler
  const handleTitleSave = useCallback(async () => {
  if (!title.trim()) {
      setTitle(channel.name);
      setIsEditingTitle(false);
      return;
  }

  if (title !== channel.name) {
      setIsSaving(true);
      try {
    const updated = await updateVoiceChannel(channel.id, { name: title });
    if (updated) {
          // Update local state
          setTitle(title);
          // Update store if item is current
          updateCurrentItem({ name: title });
    }
      } catch (error) {
    handleError(error, { level: 'error', message: 'Errore nel salvataggio del titolo del canale vocale' });
    setTitle(channel.name);
      } finally {
    setIsSaving(false);
      }
  }
  setIsEditingTitle(false);
  }, [channel.id, channel.name, title, updateCurrentItem]);

  // Update current user's speaking status in participants
  const displayParticipants = participants.map(p => ({
  ...p,
  isSpeaking: p.id === speakingUserId,
  isMuted: p.id === CURRENT_USER.id ? isMuted : p.isMuted,
  isDeafened: p.id === CURRENT_USER.id ? isDeafened : p.isDeafened,
  }));

  const connectedCount = displayParticipants.length;

  return (
  <div className="flex-1 flex flex-col overflow-hidden bg-surface-primary">
      {/* Header */}
      <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-b border-border-primary bg-surface-secondary">
    <div className="flex items-center gap-3 min-w-0">
          <div className={clsx(
      'p-2 rounded-xl transition-colors',
      isConnected ? 'bg-accent-emerald/15' : 'bg-surface-tertiary'
          )}>
      <Headphones className={clsx(
              'w-5 h-5',
              isConnected ? 'text-accent-emerald' : 'text-text-quaternary'
      )} />
          </div>
          <div className="min-w-0 flex-1">
      {isEditingTitle ? (
              <div className="flex items-center gap-2 flex-1 min-w-0">
        <input
                  ref={titleInputRef}
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  onKeyDown={(e) => {
          if (e.key === 'Enter') handleTitleSave();
          else if (e.key === 'Escape') {
                      setTitle(channel.name);
                      setIsEditingTitle(false);
          }
                  }}
                  onBlur={handleTitleSave}
                  className={clsx(
          'flex-1 min-w-0 px-2 py-1 text-base font-semibold rounded',
          'bg-surface-primary border border-border-primary text-text-primary',
          'focus:outline-none focus:ring-2 focus:ring-accent-violet focus:border-transparent'
                  )}
        />
        <button
                  onClick={handleTitleSave}
                  className="p-1 rounded hover:bg-surface-hover text-accent-emerald"
        >
                  <Check className="w-4 h-4" />
        </button>
              </div>
      ) : (
              <button
        onClick={() => setIsEditingTitle(true)}
        className="group flex items-center gap-2 min-w-0"
              >
        <h1 className="text-base font-semibold text-text-primary truncate">{channel.name}</h1>
        <Edit3 className="w-3 h-3 text-text-quaternary opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
              </button>
      )}
      <div className="flex items-center gap-2 text-xs text-text-quaternary">
              {isConnected ? (
        <>
                  <span className="flex items-center gap-1 text-accent-emerald font-medium">
          <Signal className="w-3 h-3" />
          Connesso
                  </span>
                  <span className="text-border-primary">•</span>
        </>
              ) : null}
              <span className="flex items-center gap-1">
        <Users className="w-3 h-3" />
        {connectedCount} {connectedCount === 1 ? 'partecipante' : 'partecipanti'}
              </span>
      </div>
          </div>
    </div>
    <div className="flex items-center gap-2">
          {isSaving && (
      <Loader2 className="w-4 h-4 text-text-quaternary animate-spin" />
          )}
          {onClose && (
      <button
              onClick={onClose}
              className="p-1.5 rounded-lg hover:bg-surface-hover transition-colors text-text-quaternary hover:text-text-secondary"
              title="Chiudi canale"
      >
              <X className="w-4 h-4" />
      </button>
          )}
    </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex overflow-hidden">
    {/* Participants panel */}
    <div className="w-72 border-r border-border-primary p-3 overflow-y-auto bg-surface-secondary/30">
          <div className="flex items-center justify-between mb-3 px-1">
      <h2 className="text-xs font-semibold text-text-quaternary uppercase tracking-wide">Partecipanti</h2>
      <span className="text-xs text-text-tertiary bg-surface-tertiary px-2 py-0.5 rounded-full font-medium">
              {connectedCount}
      </span>
          </div>

          <div className="space-y-1">
      {displayParticipants.map(participant => (
              <VoiceParticipant
        key={participant.id}
        participant={participant}
        isCurrentUser={participant.id === CURRENT_USER.id}
              />
      ))}
          </div>

          {!isConnected && displayParticipants.length === 0 && (
      <div className="text-center py-8">
              <div className="w-12 h-12 rounded-full bg-surface-tertiary flex items-center justify-center mx-auto mb-3">
        <Users className="w-6 h-6 text-text-quaternary" />
              </div>
              <p className="text-sm text-text-quaternary">Nessun partecipante</p>
      </div>
          )}
    </div>

    {/* Center area */}
    <div className="flex-1 flex flex-col items-center justify-center p-6 bg-linear-to-b from-transparent to-surface-secondary/20">
          {isScreenSharing ? (
      /* Screen share view */
      <div className="w-full max-w-4xl">
              <div className="aspect-video bg-black/80 rounded-2xl border border-border-primary flex items-center justify-center shadow-2xl">
        <div className="text-center">
                  <div className="w-16 h-16 rounded-2xl bg-accent-violet/20 flex items-center justify-center mx-auto mb-4">
          <MonitorSpeaker className="w-8 h-8 text-accent-violet" />
                  </div>
                  <p className="text-white font-medium">Stai condividendo lo schermo</p>
                  <p className="text-sm text-white/60 mt-1">
          Gli altri partecipanti possono vedere il tuo schermo
                  </p>
        </div>
              </div>
      </div>
          ) : (
      /* Connection status */
      <div className="text-center max-w-sm">
              <div className={clsx(
        'w-28 h-28 rounded-full flex items-center justify-center mx-auto mb-6 transition-all',
        isConnected
                  ? 'bg-linear-to-br from-accent-emerald/30 to-accent-emerald/10 ring-4 ring-accent-emerald/20 shadow-lg shadow-accent-emerald/20'
                  : 'bg-surface-tertiary'
              )}>
        <div className={clsx(
                  'w-20 h-20 rounded-full flex items-center justify-center transition-all',
                  isConnected
          ? 'bg-accent-emerald/40'
          : 'bg-surface-hover'
        )}>
                  <Mic className={clsx(
          'w-10 h-10 transition-colors',
          isConnected ? 'text-accent-emerald' : 'text-text-quaternary'
                  )} />
        </div>
              </div>

              <h2 className="text-xl font-semibold text-text-primary mb-2">
        {isConnected ? 'Sei nel canale' : 'Canale vocale'}
              </h2>

              <p className="text-sm text-text-tertiary leading-relaxed">
        {isConnected
                  ? isMuted
          ? 'Microfono disattivato. Clicca per riattivarlo.'
          : 'Parla liberamente con gli altri partecipanti.'
                  : 'Unisciti per parlare con altri.'}
              </p>
      </div>
          )}
    </div>
      </div>

      {/* Controls bar */}
      <div className="px-4 sm:px-6 py-3 border-t border-border-primary bg-surface-secondary/80 backdrop-blur-sm">
    <VoiceControls
          isConnected={isConnected}
          isMuted={isMuted}
          isDeafened={isDeafened}
          isScreenSharing={isScreenSharing}
          onConnect={handleConnect}
          onDisconnect={handleDisconnect}
          onToggleMute={handleToggleMute}
          onToggleDeafen={handleToggleDeafen}
          onScreenShare={handleScreenShare}
    />
      </div>
  </div>
  );
}
